import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOneSignal } from "@/hooks/useOneSignal";
import { PushOptInSheet } from "@/components/PushOptInSheet";
import { Sentry } from "@/lib/sentry";
import { PUSH_OPTIN_EVENT } from "@/lib/pushOptinEvents";

// Push soft-ask, relocated to the FIRST MOMENT OF THE ESPELHO.
//
// Previously the only soft-ask was on the first daily check-in (~27% of users
// reach it), leaving push_opt_in NULL for ~86%. This reuses the SAME
// PushOptInSheet. No new i18n keys.
//
// Two triggers, same gates (push_opt_in NULL + pushSupported + not snoozed):
//  - PRIMARY (in-session): right after the user's FIRST complete exchange this
//    session (their message + a settled Mirror reply), on a TTS pause so it
//    never talks over the voice. This is what reaches D1=0% users — most never
//    return for a "later visit", so a mount-only check would never fire for them.
//  - FALLBACK (mount): a returning user who already has >= 1 mirror_message but
//    no first exchange THIS session yet. Belt-and-suspenders.
//
// Single-shot + anti-nag:
//  - the native OS prompt fires ONLY on explicit "Allow" (onAccept);
//  - "Not now" / backdrop / X writes user_profiles.push_soft_ask_snoozed_at and
//    hides the sheet for SNOOZE_DAYS. push_opt_in stays NULL (not pinned false),
//    so the gate can re-ask after the window.

const SNOOZE_DAYS = 7;
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000;

// Continuous-stability delay before the in-session sheet opens: a breathing
// pause that lets the reply settle (~1500 ms, well past the ~800 ms we'd need to
// debounce a multi-sentence TTS gap). The timer resets whenever the condition
// breaks (TTS resumes / a new turn / unmount), so it only fires on a real pause.
const IN_SESSION_OPEN_DELAY_MS = 1500;

interface MirrorPushSoftAskProps {
  // In-session signal from MirrorChat: the first full exchange (user message +
  // settled Mirror reply) has rendered. The PRIMARY trigger.
  firstExchangeComplete?: boolean;
  // True while TTS is playing — we wait for a natural pause so the sheet never
  // interrupts the Mirror's voice.
  ttsSpeaking?: boolean;
  // "I'm done with this moment" — fired the instant this sheet stops being a
  // claimant on the first Mirror moment: it resolved, it snoozed, or it
  // discovered it can never show (unsupported platform / no profile row).
  //
  // Exists because the WhatsApp soft-ask was deadlocked waiting on a condition
  // it could not observe. It read `push_opt_in` ONCE, at mount, where a D0 user
  // is always NULL — and the only thing that flips it happens later, right
  // here, with nobody re-reading. Measured: 27 gate events / 21 treatment users
  // blocked, zero ever released. This callback is the missing edge; it does NOT
  // change when or whether this sheet shows.
  onSettled?: () => void;
}

export default function MirrorPushSoftAsk({
  firstExchangeComplete = false,
  ttsSpeaking = false,
  onSettled,
}: MirrorPushSoftAskProps) {
  const [userId, setUserId] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  // Once we've shown the sheet (or resolved that it shouldn't show this mount),
  // stop re-evaluating — keeps both triggers from racing into a second show.
  const decidedRef = useRef(false);
  // Which trigger surfaced the sheet — tagged onto the telemetry so we can
  // compare conversion of the in-session vs mount placements. Set the moment we
  // decide to show, before setShow re-renders (so the prop reads the right one).
  const triggerRef = useRef<"in_session" | "mount">("in_session");
  const { isSupported, requestPermission, saveOptInToDb } = useOneSignal(userId);

  // Ref espelhado + single-shot: `onSettled` é chamado de dentro de callbacks
  // assíncronos e de efeitos, e a identidade da prop muda a cada render do pai.
  // Ler pelo ref evita closure velha (§11) e re-armar efeito à toa.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const settledRef = useRef(false);
  const settle = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    onSettledRef.current?.();
  }, []);

  // Plataforma que NÃO suporta push (iOS Safari fora de PWA, sem
  // Notification/serviceWorker): este sheet nunca vai abrir, então ele nunca vai
  // resolver `push_opt_in`, e quem esperasse por isso esperaria para sempre.
  // Cede a vez de imediato.
  useEffect(() => {
    if (!isSupported) settle();
  }, [isSupported, settle]);

  // Resolve the signed-in user once.
  useEffect(() => {
    let active = true;
    void supabase.auth.getUser().then(({ data }) => {
      if (active) setUserId(data.user?.id ?? null);
    });
    return () => { active = false; };
  }, []);

  // Shared gate. `requireDbInteraction` is the mount/returning-user path (needs
  // >= 1 historical mirror_message); the in-session path passes false because
  // the exchange that just happened IS the interaction.
  const evaluate = useCallback(async (requireDbInteraction: boolean) => {
    if (decidedRef.current || !userId || !isSupported) return;

    const { data: prof, error: profErr } = await supabase
      .from("user_profiles")
      .select("push_opt_in, push_soft_ask_snoozed_at")
      .eq("user_id", userId)
      .maybeSingle();
    // Sem linha de perfil não há o que avaliar e o sheet não abre — logo este
    // sheet não é mais candidato ao momento. Cede a vez (sem mudar o
    // comportamento do push: continua não abrindo, como antes).
    if (profErr || !prof) { settle(); return; }
    const p = prof as { push_opt_in?: boolean | null; push_soft_ask_snoozed_at?: string | null };

    // Gate 1 — only while the decision is still NULL (never asked / not pinned).
    if (p.push_opt_in === true || p.push_opt_in === false) {
      decidedRef.current = true; settle(); return;
    }

    // Gate 2 — respect an active snooze window.
    if (p.push_soft_ask_snoozed_at) {
      const snoozedAt = Date.parse(p.push_soft_ask_snoozed_at);
      if (Number.isFinite(snoozedAt) && Date.now() - snoozedAt < SNOOZE_MS) {
        decidedRef.current = true; settle(); return;
      }
    }

    // Gate 3 (mount path only) — require >= 1 historical mirror_message. Don't
    // mark decided when absent: the in-session trigger should still fire later.
    if (requireDbInteraction) {
      const { count, error: cntErr } = await supabase
        .from("mirror_messages")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId);
      if (cntErr || (count ?? 0) < 1) return;
    }

    decidedRef.current = true;
    triggerRef.current = requireDbInteraction ? "mount" : "in_session";
    setShow(true);
  }, [userId, isSupported, settle]);

  // FALLBACK — mount / returning user.
  useEffect(() => {
    void evaluate(true);
  }, [evaluate]);

  // PRIMARY — in-session, right after the first complete exchange, on a TTS
  // pause. Wait for the condition to hold STABLY for IN_SESSION_OPEN_DELAY_MS
  // before opening: the cleanup clears the pending timer on every dep change, so
  // TTS resuming (multi-sentence reply), a new turn, or the user navigating away
  // (unmount) all re-arm / cancel it — the sheet only opens on a real pause.
  useEffect(() => {
    if (!firstExchangeComplete || ttsSpeaking) return;
    const id = setTimeout(() => { void evaluate(false); }, IN_SESSION_OPEN_DELAY_MS);
    return () => clearTimeout(id);
  }, [firstExchangeComplete, ttsSpeaking, evaluate]);

  const snooze = useCallback(async () => {
    if (!userId) return;
    const { error } = await supabase
      .from("user_profiles")
      .update({ push_soft_ask_snoozed_at: new Date().toISOString() } as never)
      .eq("user_id", userId);
    if (error) {
      try {
        Sentry.captureException(error, { tags: { feature: "push_softask_snooze_failed" } });
      } catch { /* Sentry not initialised in dev */ }
    }
  }, [userId]);

  // Telemetry — tag the outcome with the trigger so we can see which placement
  // (in_session vs mount) actually converts. Logged here (not in the sheet)
  // because the native-prompt OUTCOME is only known on this side.
  const logOutcome = useCallback((event: string) => {
    try {
      Sentry.captureMessage(event, {
        level: "info",
        tags: { feature: "push_optin", trigger: triggerRef.current },
      });
    } catch { /* Sentry not initialised in dev */ }
  }, []);

  const handleAccept = useCallback(async () => {
    setShow(false);
    // Record the intent BEFORE the native prompt opens — if the user kills the
    // app while the OS dialog is up, we still see they reached it (measures the
    // native-prompt drop, the next bottleneck after placement).
    logOutcome(PUSH_OPTIN_EVENT.promptOpened);
    // The native OS permission prompt fires ONLY here, on explicit Allow.
    const result = await requestPermission();
    await saveOptInToDb(result === "granted");
    logOutcome(result === "granted" ? PUSH_OPTIN_EVENT.granted : PUSH_OPTIN_EVENT.denied);
    // Resolvido: o momento está livre para o próximo pedido, nesta sessão.
    settle();
  }, [requestPermission, saveOptInToDb, logOutcome, settle]);

  const handleDismiss = useCallback(
    async (event: typeof PUSH_OPTIN_EVENT.decline | typeof PUSH_OPTIN_EVENT.dismissed) => {
      // "Not now" / backdrop / X — snooze, never prompt, never pin push_opt_in.
      setShow(false);
      await snooze();
      logOutcome(event);
      // Adiado também libera o momento: o push não volta por SNOOZE_DAYS.
      settle();
    },
    [snooze, logOutcome, settle],
  );

  if (!show) return null;

  return (
    <PushOptInSheet
      open={show}
      trigger={triggerRef.current}
      onClose={() => { void handleDismiss(PUSH_OPTIN_EVENT.dismissed); }}
      onAccept={handleAccept}
      onDecline={() => { void handleDismiss(PUSH_OPTIN_EVENT.decline); }}
    />
  );
}
